import React from "react";
import { Button, Card, Col, Row } from "antd";
import { PlanType } from "../types/planType";
import {
  cardBody,
  callOutIconStyles,
  glowKeyframes,
  baseCardStyle,
  iconStyles,
  btnOutline,
  glowPseudoElementStyle,
  featuredCardClassName,
} from "./styles/cardStyles";
import { S3_BUCKET } from "../config";
import { PlanDesc } from "../components/PlanDesc";
import RenderPlanIcon from "./plancards/renderPlanIcon";
import { Textfit } from "react-textfit";
import OpenInNew from "../cro/icons/OpenInNew.svg";
import { useTranslation } from "react-i18next";
import { RenderFeaturedElectricBullet } from "./PlanCard";

interface PlanCardProps {
  isFeatured?: boolean;
  utilityName: string;
  utilityId?: string;
  plan: PlanType;
  isTypeVariable?: boolean;
  isTx?: boolean;
  handleSwitchCommodity: Function;
  handleShowPlanModal: Function;
}

const PlanCardSummary = ({
  plan,
  isTx,
  isFeatured = false,
  utilityId,
  isTypeVariable = false,
  utilityName,
  handleSwitchCommodity,
  handleShowPlanModal,
}: PlanCardProps) => {
  const combinedStyle: React.CSSProperties = {
    ...baseCardStyle(plan, isFeatured),
    transition: "transform 0.3s ease",
  };

  const { t, i18n } = useTranslation("cro");

  return (
    <div className="my-2">
      {isFeatured && <style>{glowKeyframes(plan)}</style>}
      {isFeatured && <style>{glowPseudoElementStyle(plan)}</style>}
      <Card
        style={combinedStyle}
        bodyStyle={{ padding: 0 }}
        className={isFeatured ? featuredCardClassName : ""}
      >
        <Row
          style={{
            ...cardBody,
            ...(isFeatured
              ? { backgroundColor: "#FFFDF1" }
              : { backgroundColor: "#E3F3FF" }),
          }}
        >
          <Col
            span={24}
            style={{
              color: "#253f66",
            }}
          >
            <Row style={{ width: "100%" }} justify={"start"} align={"top"} wrap>
              <Col
                span={24}
                style={{
                  display: "flex",
                  alignItems: isFeatured ? "flex-start" : "flex-start",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ textAlign: "left" }}>
                  <div>
                    <Textfit
                      mode="multi"
                      min={14}
                      max={20}
                      className="roboto-semibold"
                    >
                      {plan.plan_name}
                    </Textfit>
                  </div>
                  <div
                    className="roboto-semibold"
                    style={{ color: "#253f66", marginTop: "-0.5rem" }}
                  >
                    <Textfit
                      mode="single"
                      max={46}
                      style={{
                        color: "#253f66",
                        fontWeight: 500,
                      }}
                      className="roboto-bold"
                    >
                      {plan.price2000}&cent;
                      <span
                        style={{ fontSize: "1rem", fontWeight: "500" }}
                        className="roboto-bold"
                      >
                        {t("per")}
                        {String(utilityId) === "84" ? "ccf" : plan.unit}
                      </span>
                    </Textfit>
                  </div>
                </div>
                <div>
                  {isFeatured ? (
                    <div
                      style={{
                        margin: "0 auto",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <img
                          src={
                            plan.commodity_name === "Gas"
                              ? "/icon/featured-gas.png"
                              : "/icon/featured.png"
                          }
                          alt="Icon"
                          style={iconStyles}
                        />
                      </div>
                      {plan.commodity_name === "Electric" && (
                        <div
                          className="roboto-bold"
                          style={{ textAlign: "center" }}
                        >
                          <Textfit mode="multi" max={14}>
                            {t(`${plan.ef_plan_details?.callout_text}`)}
                          </Textfit>
                        </div>
                      )}
                    </div>
                  ) : (
                    <RenderPlanIcon plan={plan} utilityName={utilityName} />
                  )}
                </div>
              </Col>
              <Col span={24}>
                {plan.commodity_name === "Electric" && isTx && (
                  <p
                    className="roboto-regular"
                    style={{
                      color: "#253F66",
                      fontWeight: 450,
                    }}
                  >
                    {t("based on 2,000 kWh of estimated usage")}
                  </p>
                )}
              </Col>
            </Row>
          </Col>
        </Row>
        {plan.ef_plan_details?.plan_bullets &&
        plan.ef_plan_details?.plan_bullets.length > 0 ? (
          <div
            style={{
              padding: "1rem",
              color: "#253f66",
            }}
          >
            {isFeatured && plan.commodity_name === "Gas" && (
              <Row align="middle" gutter={[16, 16]}>
                <Col
                  span={3}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={
                      "https://d2w6juk2bn3su3.cloudfront.net/icon/piggy-bank.svg"
                    }
                    style={callOutIconStyles(plan)}
                    alt="piggy bank"
                  />
                </Col>
                <Col span={18} className="roboto-regular">
                  {i18n.language === "en"
                    ? plan.ef_plan_details?.callout_text
                    : plan.ef_plan_details?.callout_text_spanish}
                </Col>
              </Row>
            )}
            {RenderFeaturedElectricBullet(plan)}
            {plan.ef_plan_details?.plan_bullets?.map((item, index) => (
              <Row align="middle" gutter={[16, 16]} key={index}>
                <Col
                  span={3}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={S3_BUCKET + item.icon}
                    style={callOutIconStyles(plan)}
                    alt="Icon"
                  />
                </Col>
                <Col span={18} className="roboto-regular">
                  <PlanDesc desc={item.text} />
                </Col>
              </Row>
            ))}
          </div>
        ) : (
          <div
            style={{
              padding: "1rem",
              color: "#253f66",
            }}
          >
            {isFeatured && plan.commodity_name === "Gas" && (
              <Row align="middle" gutter={[16, 16]}>
                <Col
                  span={3}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={
                      "https://d2w6juk2bn3su3.cloudfront.net/icon/piggy-bank.svg"
                    }
                    style={callOutIconStyles(plan)}
                    alt="piggy bank"
                  />
                </Col>
                <Col span={18} className="roboto-regular">
                  {i18n.language === "en"
                    ? plan.ef_plan_details?.callout_text
                    : plan.ef_plan_details?.callout_text_spanish}
                </Col>
              </Row>
            )}
            <Row align="middle" gutter={[16, 16]}>
              <Col
                span={3}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={"https://d2w6juk2bn3su3.cloudfront.net/icon/lock.svg"}
                  style={callOutIconStyles(plan)}
                  alt="Lock"
                />
              </Col>
              <Col span={18} className="roboto-regular">
                {isTypeVariable
                  ? "Plan Type: Variable"
                  : t("month locked-in rate", { term: plan.contract_term })}
              </Col>
            </Row>

            <Row align="middle" gutter={[16, 16]}>
              <Col
                span={3}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={
                    plan.commodity_name === "Gas"
                      ? "https://d2w6juk2bn3su3.cloudfront.net/icon/Fire.svg"
                      : "https://d2w6juk2bn3su3.cloudfront.net/icon/check-circle-fill.svg"
                  }
                  style={callOutIconStyles(plan)}
                  alt="Renewable"
                />
              </Col>
              <Col span={18} className="roboto-regular">
                <span>
                  {plan.commodity_name === "Gas"
                    ? t("Carbon Neutral")
                    : t("100% renewable")}
                </span>
              </Col>
            </Row>

            <Row align="middle" gutter={[16, 16]}>
              <Col
                span={3}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={
                    "https://d2w6juk2bn3su3.cloudfront.net/icon/slash-circle.svg"
                  }
                  style={callOutIconStyles(plan)}
                  alt="DND"
                />
              </Col>
              <Col span={18} className="roboto-regular">
                <span>
                  {plan.commodity_name === "Gas"
                    ? t("No hidden fees")
                    : t("No base charges or usage fees")}
                </span>
              </Col>
            </Row>
          </div>
        )}
        {window.location.pathname === "/serviceAddress" ? (
          <Row
            align={"middle"}
            justify="start"
            wrap
            style={{ marginBottom: "1rem" }}
          >
            <Col style={{ marginLeft: "1rem" }}>
              <Button
                style={{
                  ...btnOutline,
                  width: "100%",
                  fontWeight: 500,
                  ...(isFeatured
                    ? { backgroundColor: plan.ef_plan_details?.callout_color }
                    : {}),
                }}
                onClick={() => {
                  handleSwitchCommodity(plan.commodity_name);
                  handleShowPlanModal();
                }}
              >
                {t("CHANGE PLAN")}
              </Button>
            </Col>
          </Row>
        ) : null}
        <Row>
          <Col span={24} style={{ padding: ".5rem 1rem" }}>
            <ul>
              {plan.docs.map((doc: any, index: number) => (
                <li key={index} style={{ marginBottom: "10px" }}>
                  <img
                    src={OpenInNew}
                    alt="open in new tab"
                    style={{ width: "1.5rem", height: "auto", marginRight: 8 }}
                  />
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      textDecoration: "underline",
                      color: "#253F66",
                      fontWeight: 400,
                    }}
                  >
                    {t(doc.display_name.trim())}
                  </a>
                </li>
              ))}
            </ul>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default PlanCardSummary;
